import { ICellRendererParams } from "ag-grid-community";
import React, { MouseEvent, FC, useRef } from "react";
import styles from "components/portfolio/cellRenderer/ExpandPlanRenderer.module.scss";
import { AxiosInstance } from "axios";
import ArrowPopper from "components/app/ArrowPopper";
import { Skeleton } from "@mui/material";
import moment from "moment";
import { LoadingStatus } from "types/app";
import dynamic from "next/dynamic";
import "moment-timezone";
import { KeyboardArrowDown, GetApp } from "@mui/icons-material";
import Link from "next/link";
import { AuthDetail } from "types/auth";
import { useState } from "react";
import { useEffect } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { CollabMapFile } from "types/summary";
import { classHOC } from "components/app/ClassWrapper";

interface CollabMapRendererProps {
  authDetail: AuthDetail;
  axios: AxiosInstance;
}

const useStyles = makeStyles(() => ({
  displayContainer: {
    display: "flex",
    flexDirection: "column",
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 12,
    color: "#121212",
  },
  label: {
    fontSize: 11,
    color: "#898989",
  },
  value: {
    fontSize: 11,
    color: "#535759",
    textAlign: "left",
  },
  linkContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 16,
  },
  layout: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  marginBottom: {
    marginBottom: 10,
  },
}));

const CollabMapRenderer: FC<ICellRendererParams & CollabMapRendererProps> = ({
  data,
  axios,
  node,
}) => {
  const classes = useStyles();
  const epCellRef = useRef<HTMLDivElement>(null);
  const epPopRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [collabMapData, setCollabMapData] = useState<CollabMapFile | undefined>(undefined);
  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>("notLoaded");

  useEffect(() => {
    const closePop = (e: Event) => {
      if (
        !epPopRef.current?.contains(e.target as Node) &&
        epPopRef.current !== e.target &&
        !epCellRef.current?.contains(e.target as Node)
      ) {
        setAnchorEl(null);
      }
    };
    document.addEventListener("click", closePop);
    return () => {
      document.removeEventListener("click", closePop);
    };
  }, []);

  const handlePopoverOpen = (event: MouseEvent<HTMLElement>) => {
    // event.stopPropagation();
    if (anchorEl) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
      // const { accountId } = data;
      (axios as AxiosInstance)
        .get<CollabMapFile>(`/api/dataservice/v1/accounts/${data?.accountId}/collab-map/latest`)
        .then((res) => {
          const data = res.data;
          setCollabMapData(data);
          setLoadingStatus("loaded");
        })
        .catch(() => {
          setLoadingStatus("failed");
        });
    }
  };

  if (node.rowPinned) return <></>;

  return (
    <div className="wrapper">
      {data?.collabMapUploadedDate ? (
        <>
          <div
            ref={epCellRef}
            onClick={handlePopoverOpen}
            style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
          >
            <span style={{ color: data?.collabMapUploadedDate ? "green" : "red" }}>Yes &nbsp;</span>
            <span>({moment(data?.collabMapUploadedDate).format("MM/DD/YYYY")})</span>
            <KeyboardArrowDown style={{ fontSize: "1rem" }} />
          </div>
        </>
      ) : (
        <span style={{ color: "red" }}>No</span>
      )}
      <ArrowPopper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="left"
        disablePortal={false}
      >
        {data?.collabMapUploadedDate ? (
          <div
            ref={epPopRef}
            style={{ overflow: "auto" }}
            className={
              collabMapData
                ? styles.discoveryRecordWrapper
                : `${styles.discoveryRecordWrapper} ${styles.loadingWrapper}`
            }
          >
            {collabMapData && loadingStatus === "loaded" ? (
              <>
                <div
                  key={collabMapData?.id}
                  className={classes.displayContainer}
                >
                  <div className={classes.layout}>
                    <span className={classes.title}>CSE Collaboration Map</span>
                    <Link
                      href={`/api/dataservice-blob/v1/accounts/${data?.accountId}/collab-map/${collabMapData?.id}`}
                      passHref={true}
                      legacyBehavior
                    >
                      <a
                        className={`${styles.pointWrapper} ${classes.marginBottom}`}
                        style={{ display: "flex", alignItems: "center" }}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <GetApp style={{ marginRight: 4, fontSize: "1rem" }} /> Download
                      </a>
                    </Link>
                  </div>
                  <span>
                    <span className={classes.label}>By:</span>
                    <span
                      className={classes.value}
                      style={{ marginLeft: 15 }}
                    >
                      {collabMapData?.creatorName} ({collabMapData?.createdBy?.split("@")?.[0]})
                    </span>
                  </span>
                  <span className={styles.discoveryRecordDate}>
                    <span className={classes.label}>Date: </span>
                    <span className={classes.value}>
                      {collabMapData?.createdDate
                        ? moment(collabMapData?.createdDate).format("MM/DD/YYYY")
                        : null}
                    </span>
                  </span>
                </div>
              </>
            ) : loadingStatus !== "failed" ? (
              <div
                className={styles.loadingWrapper}
                style={{ display: "flex", flexDirection: "column" }}
              >
                {Array.from({ length: 4 }).map((x, i) => (
                  <Skeleton
                    key={`${x}${i}`}
                    width={300}
                    height={32}
                    variant="rectangular"
                    style={{ margin: "6px 12px" }}
                  />
                ))}
              </div>
            ) : (
              <div>Sorry, fail to load data</div>
            )}
          </div>
        ) : null}
      </ArrowPopper>
    </div>
  );
};
export default dynamic(() => Promise.resolve(classHOC(CollabMapRenderer)), {
  ssr: false,
});
